import { useCallback, useMemo, useState } from 'react';
import type Project from 'src/entities/project';
import useUpdateViewOnlyLinkEnabledMutation from '@projects/queries/useUpdateViewOnlyLinkEnabledMutation';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import { trackGuestLinkSettingsChanged } from 'src/services/tracker/trackers/nac';

type UseViewOnlyLinkProps = {
	project: Project;
	withDownloads: boolean;
};

const useViewOnlyLink = ( { project, withDownloads }: UseViewOnlyLinkProps ) => {
	const [ showSuccessMessage, setShowSuccessMessage ] = useState( false );
	const snackbarSystem = useSnackbarSystem();
	const { mutate: updateViewOnlyLinkEnabled } = useUpdateViewOnlyLinkEnabledMutation( {
		projectID: project.id
	} );

	const viewOnlyLink = `${document.location.origin}/${project.shareLink( { withDownloads } )}`;

	const copyShareLink = useCallback( () => {
		navigator.clipboard.writeText( viewOnlyLink );
		setShowSuccessMessage( true );
		setTimeout( () => setShowSuccessMessage( false ), 2000 );
	}, [ viewOnlyLink ] );

	const boxStyles = useMemo( () => ( {
		borderRadius: 4,
		backgroundColor: project.viewOnlyLinkEnabled
			? 'var(--color-bg-surface-container-blue)'
			: 'var(--color-bg-container)',
		border: project.viewOnlyLinkEnabled
			? '1px solid var(--color-outline-light-blue)'
			: '1px solid var(--color-outline-light)'
	} ), [ project.viewOnlyLinkEnabled ] );

	const onLinkEnabledToggled = useCallback( () => {
		const newEnabledState = !project.viewOnlyLinkEnabled;

		updateViewOnlyLinkEnabled( newEnabledState, {
			onSuccess: () => {
				trackGuestLinkSettingsChanged( { projectID: project.id, enabled: newEnabledState, role: 'ViewOnly' } );

				if ( !newEnabledState ) {
					snackbarSystem.showInfoMessage( {
						title: 'Link disabled',
						description: 'No one will be able to access the project with this link.'
					} );
				}
			}
		} );
	}, [ project.viewOnlyLinkEnabled, project.id, updateViewOnlyLinkEnabled, snackbarSystem ] );

	return {
		showSuccessMessage,
		copyShareLink,
		boxStyles,
		onLinkEnabledToggled
	};
};

export default useViewOnlyLink;
